/*
 * IMPORTS
 */
import "react-date-range/dist/styles.css"; // Npm: React date range.
import "react-date-range/dist/theme/default.css"; // Npm: React date range.
import React from "react"; // Npm: react.js library.
import _ from "underscore"; // Npm: Utility module.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import {
  HiArrowPath,
  HiMiniArrowSmallDown,
  HiMiniArrowSmallUp,
  HiMiniChartPie,
  HiMiniChevronUpDown,
} from "react-icons/hi2"; // Npm: React icons.
import { Flex, IconButton, Text } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";

/*
 * GRAPHS
 */
import MccReadSmsQuery from "./__query__/index.mcc.readSms.query";

/*
 * OBJECTS
 */
const Index = () => {
  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const _MccReadSmsQuery = useQuery(MccReadSmsQuery, {
    variables: {
      startDate: dateRange?.selection?.startDate,
      endDate: dateRange?.selection?.endDate,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
  });
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _MccReadSmsQuery.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_MccReadSmsQuery.refetch]);

  // Return component.
  return (
    <Flex
      w="100%"
      borderRadius="20px"
      p={{ base: "12px", md: "22px" }}
      gap="12px"
      background="linear-gradient(-223deg, #BCC0FF 0%, #FFDBE7 60.69%, #FFEED4 99.80%)"
      WebkitBackgroundClip="text"
      WebkitTextFillColor="transparent"
      flexDir="column"
      justify="space-between"
      maxW="100%"
      boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.17)"
    >
      <Flex
        justify="space-between"
        gap={{ base: "6px", md: "10px", lg: "12px" }}
        flexWrap="nowrap"
        alignItems="center"
      >
        <Flex
          gap={{ base: "4px", md: "6px" }}
          alignItems="center"
          minW={0}
          flexShrink={0}
        >
          <HiMiniChartPie size="16px" color="black" />
          <Text
            as="h2"
            fontSize={{ base: "xs", md: "lg", xl: "xl" }}
            color="black"
            whiteSpace={{ base: "normal", md: "nowrap" }}
            maxW={{ base: "60px", md: "unset" }}
          >
            Platform Chart
          </Text>
        </Flex>
        <Flex
          gap={{ base: "6px", md: "8px", lg: "10px" }}
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="flex-end"
          minW={0}
        >
          <DateRangePicker
            startDate={dateRange?.selection?.startDate}
            endDate={dateRange?.selection?.endDate}
            w={{ base: "130px", md: "180px", lg: "210px", xl: "240px" }}
            fontSize="15px"
            height="max-content"
            gap="4px"
            bg="white"
            setSelectDate={setDateRange}
          />
          <IconButton
            aria-label="Refresh platform chart"
            borderRadius="8px"
            size="sx"
            minW={{ base: "32px", md: "36px" }}
            h={{ base: "30px", md: "36px" }}
            backgroundColor="purple.200"
            color="purple"
            _hover={{ bg: "purple.800", color: "white" }}
            icon={<HiArrowPath size="17px" />}
            isDisabled={isRefreshing}
            onClick={_HandleRefresh}
            flexShrink={0}
          />
        </Flex>
      </Flex>
      <Flex
        w="100%"
        alignItems="center"
        justifyContent={{ base: "flex-start", md: "space-between" }}
        p={{ base: "8px", md: "12px" }}
        gap={{ base: "8px", md: "12px" }}
        flexWrap="nowrap"
        overflowX="auto"
      >
        <Flex alignItems="center" gap="12px">
          <HiMiniChevronUpDown size="21px" color="black" />
          <Flex flexDir="row" gap="6px">
            <Text as="h5" fontSize="md" color="gray.800">
              {(
                parseFloat(
                  _MccReadSmsQuery?.data?.MccReadSms?._report?.businessmargin,
                ) || 0
              ).toFixed(2)}
            </Text>
            <Text as="h5" fontSize="md" color="gray.600" fontWeight="bold">
              Margin
            </Text>
          </Flex>
        </Flex>
        <Flex alignItems="center" gap="12px">
          <HiMiniArrowSmallDown size="21px" color="black" />
          <Flex flexDir="row" gap="6px">
            <Text as="h5" fontSize="md" color="gray.800">
              {(
                parseFloat(
                  _MccReadSmsQuery?.data?.MccReadSms?._report?.businessrevenue,
                ) || 0
              ).toFixed(2)}
            </Text>
            <Text as="h5" fontSize="md" color="gray.600" fontWeight="bold">
              Revenue
            </Text>
          </Flex>
        </Flex>
        <Flex alignItems="center" gap="12px">
          <HiMiniArrowSmallUp size="21px" color="black" />
          <Flex flexDir="row" gap="6px">
            <Text as="h5" fontSize="md" color="gray.800">
              {(
                parseFloat(
                  _MccReadSmsQuery?.data?.MccReadSms?._report?.businesscost,
                ) || 0
              ).toFixed(2)}
            </Text>
            <Text as="h5" fontSize="md" color="gray.600" fontWeight="bold">
              Cost
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORT
 */
export default Index;
